import React, { useCallback, useMemo } from 'react';
import { endOfDay } from 'date-fns';

import useToggle from '@lyearn/core/hooks/useToggle';
import { DateFormat, formatDate, formatDateWithYear } from '@lyearn/core/utils/date/formatDate';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { NucleoCalendarPlus } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { DateVariant, LinkButton, SelectedTime, Stack, Tooltip, Typography } from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';
import stopPropagation from '@/consts/stopPropogation';

import { useActionItemContext } from '../../context';
import { ActionDueDatePickerProps } from '../../types';
import { linkButtonStyles } from '../ActionItem/styles';

interface renderButtonComponentProps {
  t: TFunction;
  selectedTime: Date | undefined;
  isDisabled?: boolean;
  format?: DateFormat;
  placeholder?: string;
  disableInteractiveTooltip?: boolean;
}

const renderButtonComponent = ({
  t,
  selectedTime,
  format = DateFormat.Date,
  isDisabled,
  disableInteractiveTooltip = false,
}: renderButtonComponentProps) => (
  <LinkButton
    className={cn('p-0 w-full flex flex-row justify-start', linkButtonStyles, {
      'hover:bg-bg-hover active:bg-divider': !isDisabled,
      'flex-1': !!selectedTime,
    })}>
    {selectedTime ? (
      <Stack className="border border-transparent rounded-8 hover:border-border hover:bg-transparent active:border-primary p-8 w-full">
        <Tooltip
          disableInteractive={disableInteractiveTooltip}
          enterDelay={100}
          title={
            <Stack alignItems="center">
              <Typography className="text-interactive-secondary-active" variant="body-short-03">
                {t('Common:DueOn')}
              </Typography>
              <Typography className="ml-1" variant="body-short-03">
                : {formatDateWithYear(selectedTime, format, { showFullMonth: false })}
              </Typography>
            </Stack>
          }>
          <Typography className="truncate" color="text-text-secondary" variant="body-short-02">
            {formatDate(selectedTime, format, { showFullMonth: false })}
          </Typography>
        </Tooltip>
      </Stack>
    ) : (
      <Stack className="border border-transparent rounded-8 hover:border-border hover:bg-transparent active:border-primary p-8 w-full">
        <Tooltip
          disableInteractive={disableInteractiveTooltip}
          enterDelay={100}
          title={
            <Stack alignItems="center">
              <Typography variant="body-short-03">{t('Task:Details:AddDueDate')}</Typography>
            </Stack>
          }>
          <span className="h-full py-1">
            <NucleoCalendarPlus className="w-18 h-18" fillColorToken="icon-placeholder" />
          </span>
        </Tooltip>
      </Stack>
    )}
  </LinkButton>
);

const ActionDueDatePicker = ({
  className,
  fixedWidth = false,
  shouldDisableDate,
  format = DateFormat.Date,
}: ActionDueDatePickerProps) => {
  const { t } = useTranslation();
  const {
    actionItem: { dueDate, id },
    actions,
    isReadOnly,
    disableInteractiveTooltip = false,
  } = useActionItemContext();
  const selectedTime = useMemo(() => (dueDate ? new Date(dueDate) : undefined), [dueDate]);
  const [isPopoverOpen, { toggle }] = useToggle();

  const onOpenPopover = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      stopPropagation(e);
      toggle();
    },
    [toggle],
  );

  const onChangeDate = (date: SelectedTime) => {
    toggle();
    if (date) {
      actions?.onDueDateClick(endOfDay(date).toISOString(), id);
    } else {
      actions?.onDueDateClick(null, id);
    }
  };

  const containerClassName = cn(className, 'truncate w-full', { 'w-[10.1rem]': fixedWidth });

  if (isReadOnly) {
    return (
      <Stack className={containerClassName}>
        {renderButtonComponent({
          t,
          selectedTime,
          isDisabled: isReadOnly,
          format,
          disableInteractiveTooltip,
        })}
      </Stack>
    );
  }

  if (!isPopoverOpen) {
    return (
      <Stack className={containerClassName} onClick={onOpenPopover}>
        {renderButtonComponent({ t, selectedTime, format, disableInteractiveTooltip })}
      </Stack>
    );
  }

  return (
    <Stack className={containerClassName} onClick={stopPropagation}>
      <DatePicker
        open
        pickerProps={{
          variant: DateVariant.Date,
          selectedTime,
          onChange: onChangeDate,
        }}
        position="bottom-start"
        shouldDisableDate={shouldDisableDate}
        onOpenChange={toggle}>
        {renderButtonComponent({ t, selectedTime, format, disableInteractiveTooltip })}
      </DatePicker>
    </Stack>
  );
};

export default React.memo(ActionDueDatePicker);
